import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ArrowRight, Plus, Quote, HandHeart, Gem, Leaf, Sparkles } from "lucide-react";
import { useSiteSettings, MAP_EMBED_URL, DIRECTIONS_URL, telHref } from "@/lib/site";
import { fileUrl, youtubeId } from "@/lib/api";
import { useCms } from "@/lib/cms";
import { CountUp, TypewriterYear } from "@/components/HomeSections";

/* ---------- ABOUT (all text, images, video and FAQs editable in Admin → Content → About page) ---------- */
function Faq({ q, a, open, onToggle, index }) {
  return (
    <div className="border-b border-neutral-200" data-testid={`faq-${index}`}>
      <button onClick={onToggle} className="w-full flex items-center justify-between gap-4 py-5 text-left" aria-expanded={open}>
        <span className="text-base sm:text-lg font-medium text-neutral-900">{q}</span>
        <span className={`w-8 h-8 shrink-0 rounded-full border flex items-center justify-center transition-all ${open ? "bg-[#0E4D34] border-[#0E4D34] text-white rotate-45" : "border-neutral-300 text-[#0E4D34]"}`}><Plus size={16}/></span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <p className="pb-5 pr-12 text-neutral-600 leading-relaxed whitespace-pre-line">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.7 } };

export function About() {
  const c = useCms("about");
  const journey = useCms("home_journey");
  const site = useSiteSettings();
  const [openFaq, setOpenFaq] = React.useState(0);
  const vid = youtubeId(c.video_url);
  const gallery = (c.gallery || []).filter(Boolean);
  const faqs = (c.faqs || []).filter((f) => f.q);
  const values = (c.values || []).filter((v) => v.title);
  const stats = (c.stats || []).filter((x) => x.value);
  const VALUE_ICONS = [HandHeart, Gem, Leaf, Sparkles];

  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative h-[60vh] min-h-[420px] max-h-[640px] overflow-hidden bg-neutral-900">
        {c.hero_image && <motion.img initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 6 }} src={fileUrl(c.hero_image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70"/>}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10"/>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 flex flex-col justify-end pb-12 sm:pb-16 text-white">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }}>
            {c.hero_overline && <div className="text-xs tracking-[0.4em] uppercase mb-4 border-l-2 border-[#D4AF37] pl-3">{c.hero_overline}</div>}
            <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] mb-4">{c.hero_title}</h1>
            {c.hero_subtitle && <p className="text-base sm:text-lg max-w-2xl opacity-90">{c.hero_subtitle}</p>}
          </motion.div>
        </div>
      </section>

      {/* HOW IT STARTED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div {...fadeUp}>
            <div className="overline mb-3">Where it began</div>
            <h2 className="h2-editorial mb-6">{c.story_title}</h2>
            <div className="space-y-4 text-neutral-700 leading-relaxed text-[15px] sm:text-base">
              {String(c.story_text || "").split(/\n\s*\n/).filter(Boolean).map((para, i) => <p key={i} className="whitespace-pre-line">{para}</p>)}
            </div>
          </motion.div>
          {c.story_image && (
            <motion.div {...fadeUp} className="relative max-w-md mx-auto w-full">
              <div className="absolute -inset-4 rounded-[28px] border border-[#D4AF37]/60 -translate-x-4 translate-y-4"/>
              <img src={fileUrl(c.story_image)} alt="" className="relative w-full aspect-[4/5] object-cover rounded-[24px] shadow-[0_30px_60px_-25px_rgba(14,77,52,.5)]" loading="lazy"/>
            </motion.div>
          )}
        </div>
        {c.quote && (
          <motion.figure {...fadeUp} className="mt-16 sm:mt-24 max-w-3xl mx-auto text-center">
            <Quote className="mx-auto text-[#D4AF37] mb-4" size={36} fill="currentColor"/>
            <blockquote className="font-serif text-2xl sm:text-3xl leading-snug text-neutral-900">"{c.quote}"</blockquote>
            {c.quote_author && <figcaption className="mt-4 text-xs uppercase tracking-[0.3em] text-neutral-500">{c.quote_author}</figcaption>}
          </motion.figure>
        )}
      </section>

      {/* NUMBERS */}
      {stats.length > 0 && (
        <section className="bg-[#0E4D34] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((x, i) => (
              <div key={i}>
                <CountUp value={x.value} className="block font-serif text-4xl sm:text-5xl text-[#E9D8A6]"/>
                <div className="mt-2 text-xs uppercase tracking-[0.25em] opacity-80">{x.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* THE JOURNEY (same timeline as the home page) */}
      {(journey.items || []).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-24">
          <div className="text-center mb-12">
            <div className="overline mb-3">{journey.overline || "Our Journey"}</div>
            <h2 className="h2-editorial">{journey.title}</h2>
          </div>
          <div className="relative grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="hidden lg:block absolute top-[2.1rem] left-[12%] right-[12%] h-px bg-[#0E4D34]/20"/>
            {journey.items.map((it, i) => (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.6, delay: i * 0.1 }} className="relative text-center px-2">
                <div className="relative mx-auto w-[4.25rem] h-[4.25rem] rounded-full bg-white border-2 border-[#D4AF37] flex items-center justify-center text-[#0E4D34] font-semibold text-sm shadow-sm mb-5">
                  <TypewriterYear text={it.year}/>
                </div>
                <h3 className="font-serif text-xl mb-2">{it.title}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed">{it.body}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* VALUES */}
      {values.length > 0 && (
        <section className="bg-[#FAF6EE]">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-24">
            <div className="text-center mb-12">
              <div className="overline mb-3">What we stand for</div>
              <h2 className="h2-editorial">Our values</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {values.map((v, i) => {
                const Icon = VALUE_ICONS[i % VALUE_ICONS.length];
                return (
                  <motion.div key={i} {...fadeUp} transition={{ duration: 0.6, delay: i * 0.08 }}
                              className="bg-white rounded-2xl p-6 border border-[#0E4D34]/10 hover:shadow-[0_18px_40px_-20px_rgba(14,77,52,.45)] hover:-translate-y-1 transition-all duration-300">
                    <span className="w-11 h-11 rounded-full bg-[#E8F3EE] text-[#0E4D34] flex items-center justify-center mb-4"><Icon size={20}/></span>
                    <h3 className="font-semibold text-lg mb-2">{v.title}</h3>
                    <p className="text-sm text-neutral-600 leading-relaxed">{v.text}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* VIDEO — shown only after the super admin adds a YouTube link in Admin → Content → About page */}
      {vid && (
        <section className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-24" data-testid="about-video">
          <div className="text-center mb-10">
            <div className="overline mb-3">Watch</div>
            <h2 className="h2-editorial mb-3">{c.video_title}</h2>
            {c.video_text && <p className="text-neutral-600 max-w-2xl mx-auto">{c.video_text}</p>}
          </div>
          <div className="aspect-video rounded-3xl overflow-hidden bg-neutral-900 shadow-[0_30px_70px_-30px_rgba(0,0,0,.6)]">
            <iframe title={c.video_title || "Our story"} src={`https://www.youtube-nocookie.com/embed/${vid}?rel=0&modestbranding=1`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
                    className="w-full h-full border-0"/>
          </div>
        </section>
      )}

      {/* GALLERY */}
      {gallery.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-20" data-testid="about-gallery">
          <div className="text-center mb-10">
            <div className="overline mb-3">Gallery</div>
            <h2 className="h2-editorial">{c.gallery_title}</h2>
          </div>
          <div className="columns-2 md:columns-3 lg:columns-4 gap-3 sm:gap-4 [&>*]:mb-3 sm:[&>*]:mb-4">
            {gallery.map((src, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.06 }}
                          className="break-inside-avoid rounded-2xl overflow-hidden image-hover-zoom bg-neutral-100">
                {/* full picture, never cropped — the masonry columns adapt to each photo's height */}
                <img src={fileUrl(src)} alt="" loading="lazy" className="block w-full h-auto"/>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* FAQs */}
      {faqs.length > 0 && (
        <section className="bg-[#FAF9F6]">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-24 grid lg:grid-cols-3 gap-10">
            <div>
              <div className="overline mb-3">FAQs</div>
              <h2 className="h2-editorial mb-4">{c.faq_title}</h2>
              <p className="text-neutral-600 mb-6">Can't find your answer? We're happy to help.</p>
              <Link to="/contact" className="btn-outline-silk rounded-[10px]">Contact us</Link>
            </div>
            <div className="lg:col-span-2" data-testid="about-faqs">
              {faqs.map((f, i) => <Faq key={i} index={i} q={f.q} a={f.a} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}/>)}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-20">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0E4D34] to-[#1D6B4C] text-white p-8 sm:p-14 text-center">
          <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#D4AF37]/20 blur-3xl"/>
          <h2 className="relative font-serif text-3xl sm:text-4xl mb-3">{c.cta_title}</h2>
          {c.cta_text && <p className="relative opacity-90 max-w-xl mx-auto mb-8">{c.cta_text}</p>}
          <div className="relative flex flex-wrap gap-3 justify-center">
            <Link to="/shop" className="inline-flex items-center rounded-[10px] bg-white text-[#0E4D34] px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#E9D8A6]">Shop the collection</Link>
            <a href={DIRECTIONS_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-[10px] border border-white/70 px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-white hover:text-[#0E4D34]"><MapPin size={14}/>Get directions</a>
          </div>
          {site.hours && <div className="relative mt-6 text-sm opacity-80 whitespace-pre-line">{site.hours}</div>}
        </div>
      </section>
    </div>
  );
}

export function Contact() {
  const site = useSiteSettings();
  const cards = [
    { icon: MapPin, title: "Visit the Boutique", body: site.address || site.name, action: { label: "Get directions", href: DIRECTIONS_URL, external: true }, testid: "contact-card-address" },
    site.phone && { icon: Phone, title: "Call Us", body: site.phone, action: { label: "Call now", href: telHref(site.phone) }, testid: "contact-card-phone" },
    { icon: Mail, title: "Email", body: site.email, action: { label: "Send an email", href: `mailto:${site.email}` }, testid: "contact-card-email" },
    site.hours && { icon: Clock, title: "Store Hours", body: site.hours, testid: "contact-card-hours" },
  ].filter(Boolean);

  return (
    <div className="pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 sm:pt-14 pb-8 sm:pb-10">
        <div className="overline mb-3">Contact</div>
        <h1 className="h1-editorial mb-3">Come visit us</h1>
        <p className="text-neutral-600 max-w-2xl">Questions, a styling appointment or help choosing a saree — reach us any way you like, or drop by the boutique.</p>
      </div>

      {/* Full-width map */}
      <section className="relative w-full h-[55vh] min-h-[360px] max-h-[620px] bg-neutral-200" data-testid="contact-map">
        <iframe title={`${site.name} on Google Maps`} src={MAP_EMBED_URL} className="absolute inset-0 w-full h-full border-0"
                allowFullScreen loading="lazy" referrerPolicy="strict-origin-when-cross-origin"/>
      </section>

      {/* Details as cards */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 -mt-12 sm:-mt-16">
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5">
          {cards.map((c) => (
            <div key={c.title} data-testid={c.testid} className="bg-white rounded-2xl border border-neutral-200/70 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.18)] p-6 flex flex-col">
              <div className="w-11 h-11 rounded-full bg-[#E8F3EE] text-[#0E4D34] flex items-center justify-center mb-4"><c.icon size={20}/></div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-1.5">{c.title}</div>
              <div className="text-[15px] text-neutral-900 leading-relaxed whitespace-pre-line break-words flex-1">{c.body}</div>
              {c.action && (c.action.to ? (
                <Link to={c.action.to} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0E4D34] hover:underline">{c.action.label}<ArrowRight size={14}/></Link>
              ) : (
                <a href={c.action.href} {...(c.action.external ? { target: "_blank", rel: "noreferrer" } : {})}
                   className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0E4D34] hover:underline">{c.action.label}<ArrowRight size={14}/></a>
              ))}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function Journey() {
  const videos = [
    { id: "gYcYnwFllxU", title: "The Art of Weaving Kanjivaram" },
    { id: "gYcYnwFllxU", title: "Zari Threads of Banaras" },
    { id: "gYcYnwFllxU", title: "Ilkal Handloom Story" },
  ];
  return (
    <div className="max-w-6xl mx-auto px-4 py-16">
      <div className="text-center mb-10">
        <div className="overline mb-3">Our Journey</div>
        <h1 className="h1-editorial">Stories from the loom.</h1>
      </div>
      <div className="grid md:grid-cols-2 gap-6 mb-10">
        {videos.map((v, i) => (
          <div key={i} className="aspect-video bg-neutral-900">
            <iframe title={v.title} src={`https://www.youtube.com/embed/${v.id}`} className="w-full h-full" allowFullScreen/>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- LOOKBOOK: styled outfits ("looks") with a link to shop each one (Admin → Content → Lookbook) ---------- */
function LookLink({ to, children, className }) {
  if (!to) return null;
  return /^https?:/i.test(to)
    ? <a href={to} target="_blank" rel="noreferrer" className={className}>{children}</a>
    : <Link to={to} className={className}>{children}</Link>;
}

export function Lookbook() {
  const c = useCms("lookbook");
  const looks = (c.looks || []).filter((l) => l.image || l.title);
  const occasions = [...new Set(looks.map((l) => l.occasion).filter(Boolean))];
  const [occ, setOcc] = React.useState("");
  const shown = occ ? looks.filter((l) => l.occasion === occ) : looks;
  const [hero, ...rest] = shown;
  const tips = (c.tips || []).filter((t) => t.title);

  return (
    <div data-testid="lookbook-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-12 sm:pt-16 pb-10 text-center">
        {c.overline && <div className="overline mb-3">{c.overline}</div>}
        <h1 className="h1-editorial mb-5">{c.title}</h1>
        {c.intro && <p className="text-neutral-600 max-w-2xl mx-auto leading-relaxed">{c.intro}</p>}
        {occasions.length > 1 && (
          <div className="mt-8 flex flex-wrap justify-center gap-2" data-testid="lookbook-filters">
            <button onClick={() => setOcc("")} className={`rounded-full border px-4 py-1.5 text-sm ${!occ ? "bg-[#0E4D34] border-[#0E4D34] text-white" : "border-neutral-300 hover:border-[#0E4D34]"}`}>All looks</button>
            {occasions.map((o) => (
              <button key={o} onClick={() => setOcc(o)} className={`rounded-full border px-4 py-1.5 text-sm ${occ === o ? "bg-[#0E4D34] border-[#0E4D34] text-white" : "border-neutral-300 hover:border-[#0E4D34]"}`}>{o}</button>
            ))}
          </div>
        )}
      </section>

      {hero && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-12">
          <motion.div key={hero.title + occ} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                      className="grid md:grid-cols-2 gap-0 rounded-3xl overflow-hidden bg-[#FAF6EE] border border-[#0E4D34]/10">
            <div className="aspect-[4/5] md:aspect-auto md:min-h-[520px] image-hover-zoom">
              {hero.image && <img src={fileUrl(hero.image)} alt={hero.title} className="w-full h-full object-cover"/>}
            </div>
            <div className="p-8 sm:p-12 flex flex-col justify-center">
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#991B1B] mb-3">Look 01{hero.occasion ? ` · ${hero.occasion}` : ""}</div>
              <h2 className="font-serif text-3xl sm:text-5xl leading-tight mb-4">{hero.title}</h2>
              {hero.description && <p className="text-neutral-600 leading-relaxed mb-8 text-base sm:text-lg">{hero.description}</p>}
              <LookLink to={hero.link} className="btn-silk rounded-[10px] self-start">{hero.link_text || "Shop the look"}<ArrowRight size={14} className="ml-2"/></LookLink>
            </div>
          </motion.div>
        </section>
      )}

      {rest.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-16">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {rest.map((l, i) => (
              <motion.article key={l.title + i + occ} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08 }}
                              className="group" data-testid={`look-${i + 1}`}>
                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-neutral-200 mb-4 shadow-sm group-hover:shadow-[0_24px_50px_-18px_rgba(14,77,52,.5)] transition-shadow duration-300">
                  {l.image && <img src={fileUrl(l.image)} alt={l.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"/>}
                  <span className="absolute top-3 left-3 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#0E4D34]">Look {String(i + 2).padStart(2, "0")}</span>
                  {l.occasion && <span className="absolute top-3 right-3 rounded-full bg-[#0E4D34]/85 text-white px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em]">{l.occasion}</span>}
                </div>
                <h3 className="font-serif text-2xl mb-1.5">{l.title}</h3>
                {l.description && <p className="text-sm text-neutral-600 leading-relaxed mb-3">{l.description}</p>}
                <LookLink to={l.link} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0E4D34] hover:underline">{l.link_text || "Shop the look"}<ArrowRight size={14}/></LookLink>
              </motion.article>
            ))}
          </div>
        </section>
      )}

      {tips.length > 0 && (
        <section className="bg-[#0E4D34] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-20">
            <div className="text-center mb-10">
              <div className="text-xs uppercase tracking-[0.4em] text-[#E9D8A6] mb-3">From our stylists</div>
              <h2 className="font-serif text-3xl sm:text-4xl">{c.tips_title}</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {tips.map((t, i) => (
                <div key={i} className="rounded-2xl border border-white/15 bg-white/5 p-6">
                  <div className="font-serif text-4xl text-[#D4AF37] mb-3">{String(i + 1).padStart(2, "0")}</div>
                  <h3 className="font-semibold text-lg mb-2">{t.title}</h3>
                  <p className="text-sm opacity-85 leading-relaxed">{t.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {c.cta_title && (
        <section className="max-w-4xl mx-auto px-4 py-16 sm:py-20 text-center">
          <h2 className="h2-editorial mb-3">{c.cta_title}</h2>
          {c.cta_text && <p className="text-neutral-600 mb-8">{c.cta_text}</p>}
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/shop" className="btn-silk rounded-[10px]">Shop all sarees</Link>
          </div>
        </section>
      )}
    </div>
  );
}
